import Link from 'next/link';
import { memo } from 'react';

import { ExternalLinkIcon } from '@air/design-icons';
import { FileStatusIconCompleted, FileStatusSubtitle, FileStatusTitle } from '@air/feature-file-status/ui';

import { getTaskPaneFileStatusIconSize } from '~/components/FileStatusTrackingPane/getTaskPaneFileStatusIconSize';
import { PaneItemTW } from '~/components/FileStatusTrackingPane/PaneItemTW';
import { TaskPaneActionLink } from '~/components/FileStatusTrackingPane/TaskPaneActionLink';
import { TaskPaneDismissButton } from '~/components/FileStatusTrackingPane/TaskPaneDismissButton';
import { createBoardRoute } from '~/constants/routes';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { type DuplicateBoardTask, type GetCompletedMetadata } from '~/store/tasks/types';

export type DuplicateBoardCompletedPanelItemProps = {
  metadata: GetCompletedMetadata<DuplicateBoardTask>;
  onClear: () => void;
};

export const DuplicateBoardCompletedPanelItem = memo(
  ({ metadata: { sourceBoardName, duplicatedRootBoards }, onClear }: DuplicateBoardCompletedPanelItemProps) => {
    const { ui2026 } = useTemporaryFeatureFlags();
    const boardId = duplicatedRootBoards?.[0]?.id;
    const boardTitle = duplicatedRootBoards?.[0]?.title;
    const boardUrl = boardId ? createBoardRoute(boardId, boardTitle) : null;
    const subtitleName = boardTitle || sourceBoardName;

    return (
      <PaneItemTW
        truncate={false}
        avatar={<FileStatusIconCompleted size={getTaskPaneFileStatusIconSize(ui2026)} />}
        title={
          <FileStatusTitle data-testid="DUPLICATED_SUCESSFULY_TITLE">
            {ui2026 ? 'Board duplicated' : 'Duplicated successfully'}
          </FileStatusTitle>
        }
        subtitle={
          ui2026 ? (
            <FileStatusSubtitle className="truncate">{subtitleName}</FileStatusSubtitle>
          ) : (
            <FileStatusSubtitle className="truncate">
              Board &quot;
              {boardUrl ? (
                <Link
                  className="font-semibold ads:text-accent text-altitude-9 ads:hover:text-accent-hover hover:text-altitude-11 hover:underline"
                  href={boardUrl}
                  rel="noopener noreferrer"
                >
                  {boardTitle}
                </Link>
              ) : (
                sourceBoardName
              )}
              &quot; is ready to use.
            </FileStatusSubtitle>
          )
        }
        buttons={
          <>
            {ui2026 && boardUrl ? (
              <TaskPaneActionLink
                aria-label={`View duplicated board ${subtitleName}`}
                data-testid="DUPLICATED_BOARD_VIEW_LINK"
                href={boardUrl}
                icon={<ExternalLinkIcon size="sm" />}
                rel="noopener noreferrer"
              >
                View
              </TaskPaneActionLink>
            ) : null}
            <TaskPaneDismissButton label="Clear" onClick={onClear} />
          </>
        }
      />
    );
  },
);

DuplicateBoardCompletedPanelItem.displayName = 'DuplicateBoardCompletedPanelItem';
